import { Typenames } from '@varo/graphql-schema-types';
import {
  NativeStackHeaderProps,
  Stack,
  useLocalSearchParams,
  useRouter,
} from 'expo-router';
import useTransferConfirm from 'features/moveMoney/screens/transfer/hooks/useTransferConfirm';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Button from 'shared/components/vxl/Button/Button';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import ListItem from 'shared/components/vxl/ListItem/ListItem';
import { ListItemLineFormat } from 'shared/components/vxl/ListItem/ListItemTextStack';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import BodyXSText from 'shared/components/vxl/typography/primary/body/BodyXSText';
import TitleLgText from 'shared/components/vxl/typography/primary/title/TitleLgText';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import { useDefaultErrorStrings } from 'shared/hooks/errorFormatters/useDefaultError/useDefaultErrorStrings';
import useRefreshHomeTiles from 'shared/hooks/homeTiles/useRefreshHomeTiles';
import ErrorScreen, {
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';
import { formatCurrency } from 'shared/utils/string/currency';
import useVaroOnboardingFlow from 'shared/utils/useVaroOnboardingFlow/useVaroOnboardingFlow';

type TransferConfirmSearchParams = {
  amount: string;
  currency: string;
  destinationAccountId: string;
  sourceAccountId: string;
  sourceAccountName: string;
  destinationAccountName: string;
  date: string;
  transferEstimate: string;
  fromAccountType: string;
  toAccountType: string;
  fromBank: string;
  toBank: string;
  transferType: string;
  fromAccountId: string;
  toAccountId: string;
  flowId?: string;
  source?: string;
};

const TransferConfirm = () => {
  const { t: translatedText } = useTranslation('moveMoney', {
    keyPrefix: 'transferStrings',
  });
  const { bottom } = useSafeAreaInsets();
  const router = useRouter();
  const [homeTiles, setHomeTiles] = useState<string[]>([]);

  const {
    amount,
    currency,
    destinationAccountId,
    sourceAccountId,
    sourceAccountName,
    destinationAccountName,
    date,
    transferEstimate,
    fromAccountType,
    toAccountType,
    fromBank,
    toBank,
    transferType,
    fromAccountId,
    toAccountId,
    flowId,
    source,
  } = useLocalSearchParams<TransferConfirmSearchParams>();

  const flow = useVaroOnboardingFlow();

  const { data, error, submitTransfer, fetching } = useTransferConfirm();
  const submitTransferData = data?.submitTransfer;

  const {
    data: tilesData = {},
    refreshTiles,
    fetching: refreshFetching,
    error: refreshError,
  } = useRefreshHomeTiles(homeTiles);

  useEffect(() => {
    if (submitTransferData?.__typename === Typenames.SubmitTransferSuccess) {
      setHomeTiles(submitTransferData.tileIdsToRefresh);
    }
  }, [submitTransferData]);

  useEffect(() => {
    if (homeTiles.length !== 0) {
      refreshTiles();
    }
  }, [homeTiles, refreshTiles]);

  useEffect(() => {
    if (
      submitTransferData?.__typename === Typenames.SubmitTransferSuccess &&
      ((typeof tilesData === 'object' &&
        tilesData !== null &&
        Object.keys(tilesData).length > 0) ||
        refreshError)
    ) {
      router.dismiss();
      router.dismissTo({
        pathname: `/${flow}/transfer/transfer-success`,
        params: {
          amount,
          fromAccountId,
          toAccountId,
          fromAccountType,
          toAccountType,
          fromBank,
          toBank,
          transferType,
          ...(flowId && { flowId }),
          ...(source && { source }),
        },
      });
    } else if (
      error ||
      submitTransferData?.__typename === Typenames.SubmitTransferError
    ) {
      router.replace(`/${flow}/transfer/transfer-error`);
    }
  }, [
    amount,
    submitTransferData,
    error,
    fromAccountId,
    fromAccountType,
    fromBank,
    toAccountId,
    toAccountType,
    toBank,
    transferType,
    tilesData,
    refreshError,
    flow,
    flowId,
    router,
    source,
  ]);

  const transferMoneySubmitted = usePressableAnalytics({
    analyticsId: 'transferMoneySubmitted',
    customAttributes: {
      from_account_id: fromAccountId,
      to_account_id: toAccountId,
      from_account_type: fromAccountType,
      to_account_type: toAccountType,
      from_bank: fromBank,
      to_bank: toBank,
      transfer_amount: amount,
      transfer_type: transferType,
    },
  });

  const handleSubmit = async () => {
    transferMoneySubmitted();

    try {
      await submitTransfer(
        sourceAccountId,
        destinationAccountId,
        amount,
        currency,
      );
    } catch {
      router.replace(`/${flow}/transfer/transfer-error`);
    }
  };

  const confirmText = translatedText('confirmText', {
    amount: formatCurrency(amount, true) ?? '',
    sourceAccountName: sourceAccountName ?? '',
    destinationAccountName: destinationAccountName ?? '',
    date: date ?? '',
  });

  const { defaultTitle, defaultMessage, cancelButtonTitle } =
    useDefaultErrorStrings();

  const cancel = () => {
    router.back();
  };

  const buttons = {
    primaryButton: {
      title: cancelButtonTitle,
      onPress: cancel,
    },
  };

  const errorModel = error?.message
    ? {
        screenType: ErrorScreenType.Error,
        messageHeadline: defaultTitle,
        messageBody: error.message ?? defaultMessage,
        ...buttons,
      }
    : null;

  useViewDidFocusScreenAnalyticsEvent({
    key: 'transferMoneyConfirm',
    customAttributes: {
      alert_name: 'suspicious_transaction_alert_banner',
    },
  });

  return (
    <View style={[styles.screenContainer, { paddingBottom: bottom }]}>
      <Stack.Screen
        options={{
          headerShown: !error,
          headerBackVisible: !error,
          title: translatedText('title'),
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar {...props} />
          ),
        }}
      />
      <ScreenTransitionOverlay
        isLoading={false}
        LoadingComponent={<LoadingSpinner />}
        ErrorComponent={
          errorModel && !fetching ? <ErrorScreen {...errorModel} /> : null
        }
      />
      <View style={styles.container}>
        <TitleLgText numberOfLines={0} style={styles.titleText}>
          {translatedText('confirmTitle')}
        </TitleLgText>
        <ListItem
          primaryCopy={formatCurrency(amount.toString(), true)}
          secondaryCopy={translatedText('amount')}
          showsChevron={false}
          lineFormat={ListItemLineFormat.LABEL}
        />
        <View style={styles.titleText}>
          <HairLineDivider />
        </View>
        <ListItem
          primaryCopy={transferEstimate}
          secondaryCopy={translatedText('arrives')}
          showsChevron={false}
          lineFormat={ListItemLineFormat.LABEL}
        />
      </View>
      <View style={styles.buttonView}>
        <BodyXSText numberOfLines={0} style={styles.confirmText}>
          {confirmText}
        </BodyXSText>
        <Button
          label={translatedText('confirmButton')}
          onPress={() => {
            void handleSubmit();
          }}
          isLoading={refreshFetching || fetching}
        />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  confirmText: {
    marginVertical: theme.spacing.m,
    color: theme.color.text.secondary,
  },
  buttonView: {
    padding: theme.spacing.m,
  },
  container: {
    flex: 1,
    paddingVertical: theme.spacing.m,
    width: '100%',
  },
  screenContainer: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  titleText: {
    paddingHorizontal: theme.spacing.m,
  },
});

export default TransferConfirm;
